import { useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Info, Plus, Trash2 } from "lucide-react";
import Calendar from "./calendar";
import TimeComponent from "./time-component";
import cargandoCard from "./cargando-card";
import { FORMULARIO_VIAJE_KEY } from "../utils/const/formulario-const";
import { COLOR_AZUL_MAIN } from "../utils/const/colors-const";
import { ESTILO_BOTON_MODAL_ACEPTAR } from "../utils/const/estilos-const";
import { obtenerListaTransportadora } from "../api/api-transportadora";
import { obtenerTodasLasUnidades } from "../api/gestion-dts/gestion";
import {
  obtenerGeocercasPorIdGrupoGeocerca,
  obtenerListaGrupoGeocerca,
} from "../api/api-grupo-geocerca";
import { obtenerListaProducto } from "../api/api-producto";
import { obtenerListaUnidadMedida } from "../api/api-unidad-medida";
import { obtenerListaTipoCarguio } from "../api/api-tipo-carguio";
import {
  obtenerUsuarioMovilTipoChofer,
  obtenerInfoConductorConNumeroPlaca,
} from "../api/api-usuario-movil";
import {
  actualizarSolicitudProducto,
  obtenerListaSolicitudProductoAprobado,
} from "../api/api-solicitud-producto";
import { obtenerInfoVehiculo } from "../api/api-vehiculo";
import moment from "moment";
import { useToast, useProcessing } from "../context/ui-feedback-context";
import { TIPO_INPUT, useTipoInputOptions } from "../page/viaje/tipo_input";
import { sharedIsValidDecimalNumber } from "../utils/shared-functions";

const FORM_BG = "#e8f1f8";
const FORM_BORDER = "#dadce0";
const FORM_TEXT = "#0F3457";
const FORM_REQUIRED = "#d93025";

const META_LABEL_MAX_LENGTH = 512;
const META_TEXTO_MAX_LENGTH = 1024;
const META_NOTA_MAX_LENGTH = 2048;
const META_NUMERO_MAX_LENGTH = 10;
const META_NUMERO_MAX_DECIMALS = 4;
const META_LABEL_ALLOWED_REGEX = /^[a-zA-Z0-9]*$/;
const META_LABEL_SANITIZE_REGEX = /[^a-zA-Z0-9]/g;

const parseRespuestas = (item) => {
  if (Array.isArray(item?.respuestasParsed)) return item.respuestasParsed;
  try {
    if (typeof item?.respuestas === "string" && item.respuestas.trim()) {
      const parsed = JSON.parse(item.respuestas);
      return Array.isArray(parsed) ? parsed : [];
    }
    if (Array.isArray(item?.respuestas)) return item.respuestas;
  } catch (err) {
    console.log("DetalleRequerimientoViajeComponent parseRespuestas err", err);
  }
  return [];
};

const parseMeta = (item) => {
  try {
    if (Array.isArray(item?.meta)) return item.meta;
    if (typeof item?.meta === "string" && item.meta.trim()) {
      const parsed = JSON.parse(item.meta);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (err) {
    console.log("DetalleRequerimientoViajeComponent parseMeta err", err);
  }
  return [];
};

const createMetaFieldId = () =>
  `meta-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Factor multiplicativo para pasar una unidad a kilogramos.
// Se busca por descripcion (i18n key) o por codigo/nombre en mayúsculas.
const UNIDAD_A_KG = {
  label_kilo: 1,
  label_kilogramo: 1,
  label_tonelada: 1000,
  label_libra: 0.453592,
  label_gramo: 0.001,
  KG: 1,
  KILO: 1,
  KILOGRAMO: 1,
  KILOGRAMOS: 1,
  TON: 1000,
  TN: 1000,
  TONELADA: 1000,
  TONELADAS: 1000,
  LB: 0.453592,
  LBS: 0.453592,
  LIBRA: 0.453592,
  LIBRAS: 0.453592,
  POUND: 0.453592,
  G: 0.001,
  GR: 0.001,
  GRAMO: 0.001,
  GRAMOS: 0.001,
};

const factorAKg = (unidad) => {
  if (unidad == null) return 1;
  const byDescripcion = UNIDAD_A_KG[unidad.descripcion];
  if (byDescripcion != null) return byDescripcion;
  const byCodigo = UNIDAD_A_KG[String(unidad.codigo ?? "").toUpperCase()];
  if (byCodigo != null) return byCodigo;
  const byNombre = UNIDAD_A_KG[String(unidad.nombre ?? "").toUpperCase()];
  if (byNombre != null) return byNombre;
  return 1;
};

const unidadLabel = (unidad, t) => {
  if (unidad == null) return "kg";
  try {
    const desc = unidad.descripcion ? t(unidad.descripcion) : null;
    if (desc && desc !== unidad.descripcion) return desc;
  } catch (_) {}
  return unidad.nombre ?? unidad.codigo ?? "kg";
};

/**
 * Editable detalle de una solicitud/programacion.
 * Recibe `item` (con respuestas) y permite editar/guardar como programacion-camiones step 2.
 */
export default function DetalleRequerimientoSolicitudProductoComponent({
  item = null,
  onSaved,
}) {
  useEffect(() => {
    console.log("item", item);
  }, [item]);

  const { t } = useTranslation();
  const { showToast } = useToast();
  const { showProcessing, hideProcessing } = useProcessing();

  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [metaFields, setMetaFields] = useState([]);
  const [tipoInputSelected, setTipoInputSelected] = useState(null);
  const [metaValidationVisible, setMetaValidationVisible] = useState(false);

  const [listaGeocercaOrigenes, setListaGeocercaOrigenes] = useState([]);
  const [listaGeocercaDestinos, setListaGeocercaDestinos] = useState([]);
  const [listaGeocercaGeoruta, setListaGeocercaGeoruta] = useState([]);
  const [listaVehiculos, setListaVehiculos] = useState([]);
  const [listaProductos, setListaProductos] = useState([]);
  const [listaUnidades, setListaUnidades] = useState([]);
  const [listaSolicitudesAprobadas, setListaSolicitudesAprobadas] = useState(
    [],
  );
  const [modalCapacidad, setModalCapacidad] = useState(null); // { placa, capacidad, total, exceso, detalles: [] } | null

  const listaTipoInput = useTipoInputOptions();

  useEffect(() => {
    if (item !== null) {
      // console.log("item", item);
    }
  }, [item]);

  useEffect(() => {
    const loadCatalogos = async () => {
      setCargandoCatalogos(true);
      try {
        const [
          responseGrupoGeocerca,
          unidades,
          productos,
          aprobadas,
          unidadesMedida,
        ] = await Promise.all([
          obtenerListaGrupoGeocerca({ habilitado: 1 }, true, t),
          obtenerTodasLasUnidades(),
          obtenerListaProducto({ habilitado: 1 }, false),
          obtenerListaSolicitudProductoAprobado({ habilitado: 1 }, false, t),
          obtenerListaUnidadMedida({ habilitado: 1 }, false, t),
        ]);

        setListaVehiculos(Array.isArray(unidades) ? unidades : []);
        setListaProductos(Array.isArray(productos) ? productos : []);
        setListaSolicitudesAprobadas(Array.isArray(aprobadas) ? aprobadas : []);
        setListaUnidades(Array.isArray(unidadesMedida) ? unidadesMedida : []);

        let idGrupoOrigen = null;
        let idGrupoDestino = null;
        let idGrupoGeoruta = null;
        if (Array.isArray(responseGrupoGeocerca)) {
          idGrupoOrigen = responseGrupoGeocerca.find(
            (g) => g.idConceptoGeocerca === 10,
          )?.idGrupoGeocerca;
          idGrupoDestino = responseGrupoGeocerca.find(
            (g) => g.idConceptoGeocerca === 2,
          )?.idGrupoGeocerca;
          idGrupoGeoruta = responseGrupoGeocerca.find(
            (g) => g.idConceptoGeocerca === 4,
          )?.idGrupoGeocerca;
        }

        const [geocercasOrigen, geocercasDestino, geocercasGeoruta] = await Promise.all([
          obtenerGeocercasPorIdGrupoGeocerca({
            habilitado: 1,
            idGrupoGeoCerca: idGrupoOrigen,
          }),
          obtenerGeocercasPorIdGrupoGeocerca({
            habilitado: 1,
            idGrupoGeoCerca: idGrupoDestino,
          }),
          obtenerGeocercasPorIdGrupoGeocerca({
            idGrupoGeoCerca: idGrupoGeoruta,
          }),
        ]);

        if (
          geocercasOrigen.error === false &&
          Array.isArray(geocercasOrigen.body)
        ) {
          setListaGeocercaOrigenes(geocercasOrigen.body);
        }
        if (
          geocercasDestino.error === false &&
          Array.isArray(geocercasDestino.body)
        ) {
          setListaGeocercaDestinos(geocercasDestino.body);
        }
        if (
          geocercasGeoruta.error === false &&
          Array.isArray(geocercasGeoruta.body)
        ) {
          setListaGeocercaGeoruta(geocercasGeoruta.body);
        }
      } catch (err) {
        console.log(
          "DetalleRequerimientoViajeComponent loadCatalogos err",
          err,
        );
      } finally {
        setCargandoCatalogos(false);
      }
    };

    loadCatalogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!item) {
      setDynamicFields([]);
      setMetaFields([]);
      setTipoInputSelected(null);
      return;
    }
    const fields = parseRespuestas(item)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setDynamicFields(fields);
    setMetaFields(parseMeta(item));
    setTipoInputSelected(null);
    setMetaValidationVisible(false);
  }, [item?.id]);

  const handleDynamicFieldChange = (fieldId, value) => {
    setDynamicFields((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((field) =>
        field.id === fieldId ? { ...field, value } : field,
      );
    });
  };

  const isMetaLabelValid = (label) => {
    const text = String(label ?? "").trim();
    return text.length > 0 && META_LABEL_ALLOWED_REGEX.test(text);
  };

  const handleMetaLabelChange = (fieldId, value) => {
    const sanitized = String(value ?? "")
      .replace(META_LABEL_SANITIZE_REGEX, "")
      .slice(0, META_LABEL_MAX_LENGTH);
    handleMetaFieldChange(fieldId, "label", sanitized);
  };

  const isMetaNumeroValid = (value) =>
    sharedIsValidDecimalNumber(value, {
      maxLength: META_NUMERO_MAX_LENGTH,
      maxDecimals: META_NUMERO_MAX_DECIMALS,
      allowIncomplete: false,
    });

  const handleAgregarMetaField = () => {
    if (!tipoInputSelected?.value) return;
    setMetaFields((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: createMetaFieldId(),
        type: tipoInputSelected.value,
        label: "",
        value: "",
      },
    ]);
    setTipoInputSelected(null);
  };

  const handleMetaFieldChange = (fieldId, key, value) => {
    setMetaFields((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((field) =>
        field.id === fieldId ? { ...field, [key]: value } : field,
      );
    });
  };

  const handleMetaNumeroChange = (fieldId, value) => {
    if (
      !sharedIsValidDecimalNumber(value, {
        maxLength: META_NUMERO_MAX_LENGTH,
        maxDecimals: META_NUMERO_MAX_DECIMALS,
        allowIncomplete: true,
      })
    ) {
      return;
    }
    handleMetaFieldChange(fieldId, "value", value);
  };

  const handleEliminarMetaField = (fieldId) => {
    setMetaFields((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.filter((field) => field.id !== fieldId);
    });
  };

  const validarMetaFields = () => {
    if (!Array.isArray(metaFields) || metaFields.length === 0) {
      return true;
    }

    const hasInvalidLabel = metaFields.some(
      (field) => !isMetaLabelValid(field.label),
    );
    const hasInvalidNumero = metaFields.some(
      (field) =>
        field.type === TIPO_INPUT.NUMERO &&
        String(field.value ?? "").trim() !== "" &&
        !isMetaNumeroValid(field.value),
    );

    if (hasInvalidLabel || hasInvalidNumero) {
      setMetaValidationVisible(true);
      if (hasInvalidLabel) {
        showToast(t("alert_debe_ingresar_etiqueta"), "error", 3000);
      } else {
        showToast(t("alert_numero_invalido"), "error", 3000);
      }
      return false;
    }

    setMetaValidationVisible(false);
    return true;
  };

  const googleFormInputClass =
    "w-full bg-transparent border-0 border-b border-gray-300 rounded-none px-0 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-[color:var(--form-accent)] transition-colors";

  const googleFormSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      border: "none",
      borderBottom: state.isFocused
        ? `2px solid ${COLOR_AZUL_MAIN}`
        : `1px solid ${FORM_BORDER}`,
      borderRadius: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
      "&:hover": {
        borderBottom: state.isFocused
          ? `2px solid ${COLOR_AZUL_MAIN}`
          : `1px solid #5f6368`,
      },
    }),
    valueContainer: (base) => ({ ...base, padding: "4px 0" }),
    indicatorSeparator: () => ({ display: "none" }),
    placeholder: (base) => ({ ...base, color: "#9aa0a6" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const wrapGoogleFormField = (field, control) => (
    <div
      key={field.id}
      className="group rounded-lg border bg-white shadow-sm transition-all focus-within:shadow-md"
      style={{
        borderColor: FORM_BORDER,
        ["--form-accent"]: COLOR_AZUL_MAIN,
      }}
    >
      <div
        className="h-1.5 rounded-t-lg opacity-0 transition-opacity group-focus-within:opacity-100"
        style={{ backgroundColor: COLOR_AZUL_MAIN }}
      />
      <div className="px-4 py-5 sm:px-6 sm:py-6">
        <label
          htmlFor={field.key}
          className="mb-4 block text-[16px] leading-6"
          style={{ color: FORM_TEXT }}
        >
          {field.label}
          {field.obligatorio ? (
            <span
              className="ml-1"
              style={{ color: FORM_REQUIRED }}
              aria-hidden="true"
            >
              *
            </span>
          ) : null}
        </label>
        <div className="w-full max-w-full sm:max-w-md">{control}</div>
      </div>
    </div>
  );

  const parseMetaArray = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw.trim().length > 0) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const getMetaValueByType = (meta, type) =>
    (Array.isArray(meta) ? meta : []).find((m) => m?.type === type)?.value ??
    null;

  const buildPeriodoFromMeta = (meta) => {
    const fechaSalida = getMetaValueByType(meta, TIPO_INPUT.FECHA_SALIDA);
    const horaSalida = getMetaValueByType(meta, TIPO_INPUT.HORA_SALIDA);
    const fechaLlegada = getMetaValueByType(meta, TIPO_INPUT.FECHA_LLEGADA);
    const horaLlegada = getMetaValueByType(meta, TIPO_INPUT.HORA_LLEGADA);
    if (!fechaSalida || !horaSalida || !fechaLlegada || !horaLlegada)
      return null;
    const inicio = moment(`${fechaSalida} ${horaSalida}`, "YYYY-MM-DD HH:mm");
    const fin = moment(`${fechaLlegada} ${horaLlegada}`, "YYYY-MM-DD HH:mm");
    if (!inicio.isValid() || !fin.isValid()) return null;
    return { inicio, fin };
  };

  const periodosSeSolapan = (a, b) =>
    a.inicio.isBefore(b.fin) && b.inicio.isBefore(a.fin);

  const calcularPesoDetalle = (detalle, productosById, unidadesById) => {
    // Devuelve { totalKg, desglose: [{ unidad, cantidad, pesoUnitario, pesoOriginal, pesoKg, factor }] }
    const rows = Array.isArray(detalle) ? detalle : [];
    let totalKg = 0;
    // Agrupamos por idUnidadMedida para mostrar desglose por unidad original
    const grupos = new Map();
    rows
      .filter((r) => r?.habilitado !== 0)
      .forEach((r) => {
        const cantidad = parseFloat(r?.cantidad) || 0;
        const producto = productosById.get(String(r?.idProducto));
        const pesoUnitario = parseFloat(producto?.peso) || 0;
        const idUnidad = producto?.idUnidadMedida;
        const unidad =
          idUnidad != null ? unidadesById.get(String(idUnidad)) : null;
        const factor = factorAKg(unidad);
        const pesoOriginal = cantidad * pesoUnitario;
        const pesoKg = pesoOriginal * factor;
        totalKg += pesoKg;

        const key = String(idUnidad ?? "sin-unidad");
        if (!grupos.has(key)) {
          grupos.set(key, {
            unidad,
            pesoOriginal: 0,
            pesoKg: 0,
            factor,
          });
        }
        const g = grupos.get(key);
        g.pesoOriginal += pesoOriginal;
        g.pesoKg += pesoKg;
      });
    return { totalKg, desglose: Array.from(grupos.values()) };
  };

  const computarCargaVehiculo = async (placa) => {
    if (!placa) return null;

    const responseInfo = await obtenerInfoVehiculo({ idVehiculo: placa });
    if (responseInfo?.error === true || responseInfo?.value == null)
      return null;
    const capacidad = parseFloat(responseInfo.value.capCarga);
    if (!isFinite(capacidad) || capacidad <= 0) return null;

    const periodoActual = buildPeriodoFromMeta(metaFields);

    const productosById = new Map(
      (Array.isArray(listaProductos) ? listaProductos : [])
        .filter((p) => p?.id != null)
        .map((p) => [String(p.id), p]),
    );
    const unidadesById = new Map(
      (Array.isArray(listaUnidades) ? listaUnidades : [])
        .filter((u) => u?.id != null)
        .map((u) => [String(u.id), u]),
    );

    const actual = calcularPesoDetalle(
      item?.detalle ?? [],
      productosById,
      unidadesById,
    );
    const pesoActual = actual.totalKg;

    // Sin período conocido no podemos filtrar otras — mostramos solo la actual
    const otras = periodoActual
      ? (Array.isArray(listaSolicitudesAprobadas)
          ? listaSolicitudesAprobadas
          : []
        )
          .filter((s) => s?.id != null && String(s.id) !== String(item.id))
          .filter((s) => {
            const metaOtra = parseMetaArray(s?.meta);
            const placaOtraOpt = getMetaValueByType(
              metaOtra,
              TIPO_INPUT.SELECCIONE_VEHICULO,
            );
            const placaOtra = placaOtraOpt?.value ?? null;
            if (String(placaOtra) !== String(placa)) return false;
            const periodoOtra = buildPeriodoFromMeta(metaOtra);
            if (!periodoOtra) return false;
            return periodosSeSolapan(periodoActual, periodoOtra);
          })
      : [];

    const detalles = [];
    let pesoOtras = 0;
    // Combinamos desglose por unidad de todas las otras
    const desgloseOtrasMap = new Map();
    otras.forEach((s) => {
      const info = calcularPesoDetalle(
        s?.detalle ?? [],
        productosById,
        unidadesById,
      );
      pesoOtras += info.totalKg;
      detalles.push({
        id: s.id,
        descripcion: s.descripcion ?? s.codigo ?? s.id,
        peso: info.totalKg,
        desglose: info.desglose,
      });
      info.desglose.forEach((g) => {
        const key = String(g.unidad?.id ?? "sin-unidad");
        if (!desgloseOtrasMap.has(key)) {
          desgloseOtrasMap.set(key, {
            unidad: g.unidad,
            pesoOriginal: 0,
            pesoKg: 0,
            factor: g.factor,
          });
        }
        const bucket = desgloseOtrasMap.get(key);
        bucket.pesoOriginal += g.pesoOriginal;
        bucket.pesoKg += g.pesoKg;
      });
    });

    const total = pesoActual + pesoOtras;
    const exceso = Math.max(0, total - capacidad);
    const disponible = Math.max(0, capacidad - total);
    return {
      placa,
      capacidad,
      pesoActual,
      pesoOtras,
      total,
      exceso,
      disponible,
      detalles,
      desgloseActual: actual.desglose,
      desgloseOtras: Array.from(desgloseOtrasMap.values()),
      periodoConocido: periodoActual != null,
    };
  };

  const validarCapacidadCarga = async () => {
    const placaOption = getMetaValueByType(
      metaFields,
      TIPO_INPUT.SELECCIONE_VEHICULO,
    );
    const placa = placaOption?.value ?? null;
    if (!placa) return { ok: true };
    if (!buildPeriodoFromMeta(metaFields)) return { ok: true };

    const info = await computarCargaVehiculo(placa);
    if (!info) return { ok: true };
    if (info.exceso > 0) return { ok: false, info: { ...info, esInfo: false } };
    return { ok: true };
  };

  const handleInfoCargaVehiculo = async (field) => {
    const option = field?.value;
    const placa = option?.value ?? null;
    if (!placa) return;
    showProcessing();
    const info = await computarCargaVehiculo(placa);
    hideProcessing();
    if (!info) {
      showToast(t("alert_no_se_pudo_obtener_info_vehiculo"), "error", 3000);
      return;
    }
    setModalCapacidad({ ...info, esInfo: true });
  };

  const handleActualizar = async () => {
    if (!item?.id) return;

    if (validarMetaFields() === false) {
      return;
    }

    showProcessing();
    const capacidadResult = await validarCapacidadCarga();
    console.log("capacidadResult", capacidadResult);
    hideProcessing();

    if (capacidadResult.ok === false) {
      setModalCapacidad(capacidadResult.info);
      return;
    }

    const meta = metaFields;
    let objEnviar = { ...item };
    objEnviar.meta = meta;
    console.log("entra aqui1");
    showProcessing();
    const response = await actualizarSolicitudProducto(objEnviar);
    console.log(response);
    console.log("entra aqui2");
    hideProcessing();

    if (response.error === false) {
      const updatedItem = {
        ...item,
        meta: metaFields,
      };
      showToast(t("alert_se_actualizo_correctamente"), "success", 3000);
      if (typeof onSaved === "function") {
        onSaved(updatedItem);
      }
      return;
    }

    showToast(response.message || t("alert_no_se_pudo_crear"), "error", 3000);
  };

  const getTipoInputLabel = (type) =>
    listaTipoInput.find((option) => option.value === type)?.label ?? type;

  const renderMetaValueInput = (field) => {
    const showNumeroError =
      metaValidationVisible &&
      field.type === TIPO_INPUT.NUMERO &&
      String(field.value ?? "").trim() !== "" &&
      !isMetaNumeroValid(field.value);

    switch (field.type) {
      case TIPO_INPUT.NOTA:
        return (
          <>
            <textarea
              value={field.value ?? ""}
              onChange={(e) => {
                if (e.target.value.length <= META_NOTA_MAX_LENGTH) {
                  handleMetaFieldChange(field.id, "value", e.target.value);
                }
              }}
              className={`${googleFormInputClass} resize-y min-h-[72px]`}
              placeholder={t("placeholder_ingresa_nota")}
              rows={3}
              maxLength={META_NOTA_MAX_LENGTH}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">
              {String(field.value ?? "").length}/{META_NOTA_MAX_LENGTH}
            </p>
          </>
        );
      case TIPO_INPUT.FECHA:
        return (
          <Calendar
            id={`meta-fecha-${field.id}`}
            name={`meta-fecha-${field.id}`}
            value={field.value ?? ""}
            onChange={(date) => handleMetaFieldChange(field.id, "value", date)}
            placeholder={t("placeholder_seleccione_fecha")}
          />
        );
      case TIPO_INPUT.FECHA_SALIDA:
        return (
          <Calendar
            id={`meta-fecha-salida-${field.id}`}
            name={`meta-fecha-salida-${field.id}`}
            value={field.value ?? ""}
            onChange={(date) => handleMetaFieldChange(field.id, "value", date)}
            placeholder={t("placeholder_seleccione_fecha")}
          />
        );
      case TIPO_INPUT.FECHA_LLEGADA:
        return (
          <Calendar
            id={`meta-fecha-llegada-${field.id}`}
            name={`meta-fecha-llegada-${field.id}`}
            value={field.value ?? ""}
            onChange={(date) => handleMetaFieldChange(field.id, "value", date)}
            placeholder={t("placeholder_seleccione_fecha")}
          />
        );
      case TIPO_INPUT.HORA:
        return (
          <TimeComponent
            id={`meta-hora-${field.id}`}
            name={`meta-hora-${field.id}`}
            value={field.value ?? ""}
            onChange={(time) => handleMetaFieldChange(field.id, "value", time)}
            placeholder={t("placeholder_seleccione_hora")}
          />
        );
      case TIPO_INPUT.HORA_SALIDA:
        return (
          <TimeComponent
            id={`meta-hora-salida-${field.id}`}
            name={`meta-hora-salida-${field.id}`}
            value={field.value ?? ""}
            onChange={(time) => handleMetaFieldChange(field.id, "value", time)}
            placeholder={t("placeholder_seleccione_hora")}
          />
        );
      case TIPO_INPUT.HORA_LLEGADA:
        return (
          <TimeComponent
            id={`meta-hora-llegada-${field.id}`}
            name={`meta-hora-llegada-${field.id}`}
            value={field.value ?? ""}
            onChange={(time) => handleMetaFieldChange(field.id, "value", time)}
            placeholder={t("placeholder_seleccione_hora")}
          />
        );
      case TIPO_INPUT.GEOCERCA_ORIGEN:
        return (
          <Select
            inputId={`meta-geocerca-origen-${field.id}`}
            options={listaGeocercaOrigenes}
            value={field.value || null}
            onChange={(option) =>
              handleMetaFieldChange(field.id, "value", option)
            }
            placeholder={t("placeholder_seleccione_geocerca_origen")}
            isClearable
            isSearchable
            styles={googleFormSelectStyles}
            classNamePrefix="google-form-select"
            className="text-black"
            menuPortalTarget={document.body}
          />
        );
      case TIPO_INPUT.GEOCERCA_DESTINO:
        return (
          <Select
            inputId={`meta-geocerca-destino-${field.id}`}
            options={listaGeocercaDestinos}
            value={field.value || null}
            onChange={(option) =>
              handleMetaFieldChange(field.id, "value", option)
            }
            placeholder={t("placeholder_seleccione_geocerca_destino")}
            isClearable
            isSearchable
            styles={googleFormSelectStyles}
            classNamePrefix="google-form-select"
            className="text-black"
            menuPortalTarget={document.body}
          />
        );
      case TIPO_INPUT.GEORUTA:
        return (
          <Select
            inputId={`meta-georuta-${field.id}`}
            options={listaGeocercaGeoruta}
            value={field.value || null}
            onChange={(option) =>
              handleMetaFieldChange(field.id, "value", option)
            }
            placeholder={t("placeholder_seleccione_geocerca_georuta")}
            isClearable
            isSearchable
            styles={googleFormSelectStyles}
            classNamePrefix="google-form-select"
            className="text-black"
            menuPortalTarget={document.body}
          />
        );
      case TIPO_INPUT.SELECCIONE_VEHICULO:
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <Select
                inputId={`meta-vehiculo-${field.id}`}
                options={listaVehiculos}
                value={field.value || null}
                onChange={(option) =>
                  handleMetaFieldChange(field.id, "value", option)
                }
                placeholder={t("placeholder_seleccione_vehiculo")}
                isClearable
                isSearchable
                styles={googleFormSelectStyles}
                classNamePrefix="google-form-select"
                className="text-black"
                menuPortalTarget={document.body}
              />
            </div>
            {field?.value ? (
              <button
                type="button"
                onClick={() => handleInfoCargaVehiculo(field)}
                title={t("label_ver_carga_asignada")}
                aria-label={t("label_ver_carga_asignada")}
                className="shrink-0 p-1.5 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              >
                <Info className="h-5 w-5" />
              </button>
            ) : null}
          </div>
        );
      case TIPO_INPUT.NUMERO:
        return (
          <>
            <input
              type="text"
              inputMode="decimal"
              value={field.value ?? ""}
              onChange={(e) => handleMetaNumeroChange(field.id, e.target.value)}
              className={googleFormInputClass}
              placeholder={t("label_valor")}
              maxLength={META_NUMERO_MAX_LENGTH}
            />
            {showNumeroError ? (
              <p className="text-xs mt-1" style={{ color: FORM_REQUIRED }}>
                {t("alert_numero_invalido")}
              </p>
            ) : null}
          </>
        );
      case TIPO_INPUT.TEXTO:
      default:
        return (
          <>
            <input
              type="text"
              value={field.value ?? ""}
              onChange={(e) => {
                if (e.target.value.length <= META_TEXTO_MAX_LENGTH) {
                  handleMetaFieldChange(field.id, "value", e.target.value);
                }
              }}
              className={googleFormInputClass}
              placeholder={t("label_valor")}
              maxLength={META_TEXTO_MAX_LENGTH}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">
              {String(field.value ?? "").length}/{META_TEXTO_MAX_LENGTH}
            </p>
          </>
        );
    }
  };

  if (!item) {
    return (
      <div className="h-full min-h-[300px] flex items-center justify-center text-sm text-gray-400">
        {t("label_sin_datos")}
      </div>
    );
  }

  if (cargandoCatalogos) {
    return cargandoCard();
  }

  return (
    <>
      <div
        className="rounded-lg p-3 sm:p-4"
        style={{ backgroundColor: FORM_BG }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold" style={{ color: FORM_TEXT }}>
              {item.descripcion || t("label_solicitud")}
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              {item.fechaProceso ? (
                <p className="text-sm text-gray-500">{item.fechaProceso}</p>
              ) : null}
              {item.prioridad ? (
                <p className="text-sm text-gray-500">
                  {t("label_prioridad")}: {item.prioridad}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className={ESTILO_BOTON_MODAL_ACEPTAR}
            onClick={handleActualizar}
          >
            {t("label_actualizar")}
          </button>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-semibold" style={{ color: FORM_TEXT }}>
            {t("label_productos")}
          </h4>
          {Array.isArray(item?.detalle) && item.detalle.length > 0 ? (
            item.detalle.map((detalle) => (
              <div
                key={detalle.id}
                className="rounded-lg border bg-white p-4 shadow-sm"
                style={{ borderColor: FORM_BORDER }}
              >
                <p
                  className="text-sm font-semibold leading-snug break-words"
                  style={{ color: FORM_TEXT }}
                >
                  {detalle.producto || "—"}
                </p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-500">
                    {t("label_cantidad")}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {detalle.cantidad ?? "—"}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">
              {t("label_sin_datos")}
            </div>
          )}
        </div>
      </div>

      <div
        className="rounded-lg mt-4 p-3 sm:p-4"
        style={{ backgroundColor: FORM_BG }}
      >
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <label
              className="mb-2 block text-sm font-medium"
              style={{ color: FORM_TEXT }}
            >
              {t("label_tipo")}
            </label>
            <Select
              options={listaTipoInput}
              value={tipoInputSelected}
              onChange={setTipoInputSelected}
              placeholder={t("placeholder_seleccione_tipo")}
              isClearable
              isSearchable
              styles={googleFormSelectStyles}
              classNamePrefix="google-form-select"
              className="text-black"
              menuPortalTarget={document.body}
            />
          </div>
          <button
            type="button"
            className={`${ESTILO_BOTON_MODAL_ACEPTAR} inline-flex items-center gap-2 shrink-0`}
            onClick={handleAgregarMetaField}
            disabled={!tipoInputSelected}
          >
            <Plus className="h-4 w-4" />
            {t("label_agregar")}
          </button>
        </div>

        <div className="space-y-3">
          {Array.isArray(metaFields) && metaFields.length > 0 ? (
            metaFields.map((field) => (
              <div
                key={field.id}
                className="rounded-lg border bg-white p-4 shadow-sm"
                style={{ borderColor: FORM_BORDER }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{ color: FORM_TEXT }}
                  >
                    {getTipoInputLabel(field.type)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleEliminarMetaField(field.id)}
                    className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                    title={t("label_eliminar")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="mb-2 block text-sm"
                      style={{ color: FORM_TEXT }}
                    >
                      {t("label_etiqueta")}
                      <span className="ml-1" style={{ color: FORM_REQUIRED }}>
                        *
                      </span>
                    </label>
                    <input
                      type="text"
                      value={field.label ?? ""}
                      onChange={(e) =>
                        handleMetaLabelChange(field.id, e.target.value)
                      }
                      className={googleFormInputClass}
                      placeholder={t("placeholder_ingresa_etiqueta")}
                      maxLength={META_LABEL_MAX_LENGTH}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {String(field.label ?? "").length}/{META_LABEL_MAX_LENGTH}
                    </p>
                    {metaValidationVisible && !isMetaLabelValid(field.label) ? (
                      <p
                        className="text-xs mt-1"
                        style={{ color: FORM_REQUIRED }}
                      >
                        {t("alert_debe_ingresar_etiqueta")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <label
                      className="mb-2 block text-sm"
                      style={{ color: FORM_TEXT }}
                    >
                      {t("label_valor")}
                    </label>
                    {renderMetaValueInput(field)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">
              {t("label_sin_datos")}
            </div>
          )}
        </div>
      </div>

      <div
        className="rounded-lg mt-4 p-3 sm:p-4"
        style={{ backgroundColor: FORM_BG }}
      >
        <div className="flex justify-end mt-4">
          <button
            type="button"
            className={ESTILO_BOTON_MODAL_ACEPTAR}
            onClick={handleActualizar}
          >
            {t("label_actualizar")}
          </button>
        </div>
      </div>

      {modalCapacidad && (
        <div className="fixed z-50 top-0 left-0 right-0 bottom-0 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-gray-500 opacity-75"
            onClick={() => setModalCapacidad(null)}
          ></div>
          <div className="bg-white w-11/12 md:max-w-lg mx-auto rounded shadow-lg z-50 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b border-gray-200">
              <p className="text-lg font-semibold text-gray-900">
                {modalCapacidad.esInfo
                  ? t("label_carga_asignada_vehiculo")
                  : t("alert_capacidad_carga_excedida")}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t("label_seleccione_vehiculo")}: {modalCapacidad.placa}
                {modalCapacidad.esInfo && !modalCapacidad.periodoConocido ? (
                  <span className="ml-2 text-amber-600">
                    · {t("label_sin_periodo_definido")}
                  </span>
                ) : null}
              </p>
            </div>
            <div className="px-6 py-4 overflow-y-auto space-y-3 text-sm text-gray-800">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-gray-500">
                  {t("label_capacidad_carga")}
                </span>
                <span className="text-right font-semibold">
                  {modalCapacidad.capacidad.toFixed(2)} kg
                </span>
                <span className="text-gray-500">
                  {t("label_peso_esta_solicitud")}
                </span>
                <span className="text-right">
                  {modalCapacidad.pesoActual.toFixed(2)} kg
                </span>
                <span className="text-gray-500">
                  {t("label_peso_otras_solicitudes")}
                </span>
                <span className="text-right">
                  {modalCapacidad.pesoOtras.toFixed(2)} kg
                </span>
                <span className="text-gray-500 font-semibold">
                  {t("label_peso_total")}
                </span>
                <span className="text-right font-semibold">
                  {modalCapacidad.total.toFixed(2)} kg
                </span>
                {modalCapacidad.exceso > 0 ? (
                  <>
                    <span className="text-red-600 font-semibold">
                      {t("label_exceso")}
                    </span>
                    <span className="text-right text-red-600 font-semibold">
                      {modalCapacidad.exceso.toFixed(2)} kg
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-green-700 font-semibold">
                      {t("label_disponible")}
                    </span>
                    <span className="text-right text-green-700 font-semibold">
                      {modalCapacidad.disponible.toFixed(2)} kg
                    </span>
                  </>
                )}
              </div>
              {(modalCapacidad.desgloseActual?.length > 0 ||
                modalCapacidad.desgloseOtras?.length > 0) && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    {t("label_desglose_por_unidad")}
                  </p>
                  {modalCapacidad.desgloseActual?.length > 0 && (
                    <>
                      <p className="text-xs text-gray-500 mt-1">
                        {t("label_peso_esta_solicitud")}
                      </p>
                      <ul className="text-xs text-gray-800 space-y-0.5 mt-1">
                        {modalCapacidad.desgloseActual.map((g, i) => (
                          <li key={`act-${i}`} className="flex justify-between">
                            <span>{unidadLabel(g.unidad, t)}</span>
                            <span>
                              {g.pesoOriginal.toFixed(2)}{" "}
                              <span className="text-gray-500">
                                ({g.pesoKg.toFixed(2)} kg)
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {modalCapacidad.desgloseOtras?.length > 0 && (
                    <>
                      <p className="text-xs text-gray-500 mt-2">
                        {t("label_peso_otras_solicitudes")}
                      </p>
                      <ul className="text-xs text-gray-800 space-y-0.5 mt-1">
                        {modalCapacidad.desgloseOtras.map((g, i) => (
                          <li key={`otr-${i}`} className="flex justify-between">
                            <span>{unidadLabel(g.unidad, t)}</span>
                            <span>
                              {g.pesoOriginal.toFixed(2)}{" "}
                              <span className="text-gray-500">
                                ({g.pesoKg.toFixed(2)} kg)
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
              {modalCapacidad.detalles.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-3">
                    {t("label_solicitudes_en_conflicto")}
                  </p>
                  <ul className="divide-y divide-gray-100">
                    {modalCapacidad.detalles.map((d) => (
                      <li
                        key={d.id}
                        className="py-2 flex items-start justify-between gap-3"
                      >
                        <span className="min-w-0 break-words">
                          {d.descripcion}
                        </span>
                        <span className="shrink-0 font-semibold">
                          {d.peso.toFixed(2)} kg
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
              <button
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                onClick={() => setModalCapacidad(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
